/**
 * Camera Booking helpers: role checks, date strings and calendar layout.
 *
 * The API sends and receives dates as "YYYY-MM-DD HH:MM" in the user's own
 * timezone. They are parsed by hand, not through Date, so the displayed time
 * never shifts. Date is only used for weekday names and week arithmetic.
 */

export const CAMERA_DAY_START_HOUR = 6;
export const CAMERA_DAY_END_HOUR = 22;
export const CAMERA_HOUR_PX = 48;

/** Window event fired after an approve or reject, so the menu badge refreshes. */
export const CAMERA_PENDING_CHANGED_EVENT = 'camera-pending-changed';

const pad = (n) => String(n).padStart(2, '0');

export function hasCameraFacultyRole(user) {
  return Boolean(user?.is_faculty_admin || user?.roles?.includes('faculty_admin'));
}

export function hasCameraMediaRole(user) {
  return Boolean(user?.is_camera_media || user?.roles?.includes('camera_media'));
}

/** Parse "YYYY-MM-DD HH:MM" into its parts, or null if malformed. */
export function parseBookingDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(value || '');
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  return { year, month, day, hour, minute };
}

/** Build "YYYY-MM-DD HH:MM" from a local Date. */
export function dateToBookingString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Shift a booking string by some minutes (may cross days). */
export function addMinutesToBookingString(value, minutes) {
  const parts = parseBookingDate(value);
  if (!parts) return value;
  const date = new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute + minutes);
  return dateToBookingString(date);
}

/** "YYYY-MM-DD HH:MM" -> "2026-10-20T09:00" for <input type="datetime-local">. */
export function toDateTimeLocal(value) {
  return value ? value.replace(' ', 'T') : '';
}

/** "2026-10-20T09:00" -> "2026-10-20 09:00" for the API. */
export function fromDateTimeLocal(value) {
  return value ? value.replace('T', ' ') : '';
}

/** "YYYY-MM-DD" of the day part. */
export function bookingDay(value) {
  return (value || '').slice(0, 10);
}

/** "09:00" from a booking string. */
export function formatBookingTime(value) {
  return (value || '').slice(11, 16);
}

/** "Mon 20 Oct" from a booking string. */
export function formatBookingDay(value) {
  const parts = parseBookingDate(value);
  if (!parts) return value || '';
  const date = new Date(parts.year, parts.month - 1, parts.day);
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "Mon 20 Oct, 09:00 - 11:00" (or "20 Oct 09:00 - 21 Oct 11:00" across days). */
export function formatBookingRange(start, end) {
  if (bookingDay(start) === bookingDay(end)) {
    return `${formatBookingDay(start)}, ${formatBookingTime(start)} - ${formatBookingTime(end)}`;
  }
  return `${formatBookingDay(start)} ${formatBookingTime(start)} - ${formatBookingDay(end)} ${formatBookingTime(end)}`;
}

/** Local Date for Monday 00:00 of the week containing `date`. */
export function getWeekStart(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const offset = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - offset);
  return d;
}

export function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** "YYYY-MM-DD 00:00" for the start of a local Date (API range format). */
export function toRangeString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} 00:00`;
}

/** "YYYY-MM-DD" for a local Date. */
export function toDayString(date) {
  return toRangeString(date).slice(0, 10);
}

/** Minutes past midnight for the time part of a booking string. */
export function minutesOfDay(value) {
  return Number(value.slice(11, 13)) * 60 + Number(value.slice(14, 16));
}

/**
 * Place one day's bookings into side-by-side lanes so overlapping pending
 * requests stay readable. Each entry is clipped to the day it is drawn on.
 */
export function layoutDayBookings(bookings, day) {
  const items = bookings
    .filter((b) => bookingDay(b.start) <= day && bookingDay(b.end) >= day)
    .map((booking) => ({
      booking,
      startMin: bookingDay(booking.start) === day ? minutesOfDay(booking.start) : 0,
      endMin: bookingDay(booking.end) === day ? minutesOfDay(booking.end) : 24 * 60,
    }))
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const laneEnds = [];
  const placed = items.map((item) => {
    let lane = laneEnds.findIndex((end) => end <= item.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.endMin);
    } else {
      laneEnds[lane] = item.endMin;
    }
    return { ...item, lane };
  });

  return { items: placed, laneCount: Math.max(1, laneEnds.length) };
}

/**
 * Faculties offered to a media user when booking on behalf of a faculty:
 * [{ id, name, admins: [{ id, name }] }]. Accepts the field names the
 * options endpoint may use for the admin list.
 */
export function normalizeCameraFaculties(result) {
  const list = Array.isArray(result?.faculties) ? result.faculties : [];
  return list.map((faculty) => {
    const admins = faculty.admins || faculty.faculty_admins || [];
    return {
      id: faculty.id,
      name: faculty.name || faculty.code || `Faculty ${faculty.id}`,
      admins: admins.map((admin) => ({
        id: admin.id,
        name: admin.name || admin.login || `User ${admin.id}`,
      })),
    };
  });
}

export const CAMERA_STATE_STYLES = {
  approved: {
    label: 'Approved',
    block: 'bg-green-100 border-green-500 text-green-900',
    badge: 'bg-green-100 text-green-800',
  },
  pending: {
    label: 'Pending',
    block: 'bg-amber-100 border-amber-500 text-amber-900',
    badge: 'bg-amber-100 text-amber-800',
  },
  rejected: {
    label: 'Rejected',
    block: 'bg-red-100 border-red-500 text-red-900',
    badge: 'bg-red-100 text-red-800',
  },
  cancelled: {
    label: 'Cancelled',
    block: 'bg-gray-100 border-gray-400 text-gray-700',
    badge: 'bg-gray-100 text-gray-700',
  },
};

export function getCameraStateStyle(state) {
  return CAMERA_STATE_STYLES[state] || CAMERA_STATE_STYLES.cancelled;
}

/** A booking can be cancelled while it is pending or approved. */
export function canCancelBooking(booking) {
  return booking?.state === 'pending' || booking?.state === 'approved';
}
